import { useCallback, useEffect, useRef } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { LocalStorageConfigs, loadFromLocalRaw } from "../../../utils/LocalStorage";
import { useObx } from "../../../utils/UseObx";
import ApiRequestPaths from "../../Constants/ApiRequestPaths";

export interface IUseUpsertPatientNoteProps {
  /** The id of the contact for the current application session. */
  contactId: string;
  /** The id of the patient for whom the note is being created or updated. */
  patientId: string;
  /** The id of the center the note is associated with. */
  centerId: string;
}

interface INewPatientNoteRequest {
  /** The content of the note. */
  text: string;
  /** The ID of the center the note is associated with. */
  centerId: string;
}

interface IPatientNote extends INewPatientNoteRequest {
  /** The primary identifier of the note to the application. */
  id: string;
}

/** A triggered hook for PUTing or POSTing a patient note.
 * @remarks This hook will cache the note id to local storage when a note is created or updated.
 */
const useUpsertPatientNote: TriggeredHook<IPatientNote, string, boolean, IUseUpsertPatientNoteProps> = ({
  contactId,
  centerId,
  patientId
}) => {
  const noteIdRef = useRef(
    loadFromLocalRaw(LocalStorageConfigs.patientNoteId.key) ??
      loadFromLocalRaw(LocalStorageConfigs.legacy.patientNoteId.key) ??
      null
  );
  const { setReq, res, err, loading } = useObx<IPatientNote, INewPatientNoteRequest & Partial<IPatientNote>>(
    "UpsertPatientNote"
  );

  const execute = useCallback((noteContent: string) => {
    const payload = { text: noteContent, centerId };
    setReq(
      noteIdRef.current
        ? {
            url: ApiRequestPaths.putPatientNote(contactId, patientId, noteIdRef.current),
            method: "put",
            payload: { ...payload, id: noteIdRef.current }
          }
        : {
            url: ApiRequestPaths.postPatientNote(contactId, patientId),
            method: "post",
            payload
          }
    );
  }, []);

  useEffect(() => {
    if (res?.payload) {
      noteIdRef.current = res.payload.id;
      localStorage.setItem(LocalStorageConfigs.patientNoteId.key, res.payload.id);
    }
  }, [res]);

  return { execute, result: res?.payload || null, error: Boolean(err), isLoading: loading };
};

export default useUpsertPatientNote;
